import { Button, Dropdown, Progress, Select } from "antd";
import Search from "antd/es/input/Search";
import { ColumnsType } from "antd/es/table";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import EditIcon from "../../../assets/icons/EditIcon";
import EyeIcon from "../../../assets/icons/EyeIcon";
import FilterIcon from "../../../assets/icons/FilterIcon";
import AXIOS from "../../../helpers/api";
import { BASE_API } from "../../../helpers/apiUrl";
import {
  addKeyInArray,
  ArrayOption,
  selectSearchOption,
} from "../../../helpers/siteConfig";
import { toast } from "../../../helpers/toast";
import useModal from "../../../hooks/useModal";
import {
  cancelTowerTicketAsync,
  getAllCountTowerAsync2,
  getAllTowerAsync,
  getAllTowerAsync2,
  getCountTowerAsync,
  removePGFromTowerTicketAsync,
  towerTicketStartAsync,
  towerTicketStopAsync,
} from "../../../store/features/NP/network/networkAPI";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { CustomTableProps, ModalType } from "../../../types";
import { GetAllTowerData, TableData } from "../../../types/redux";
import { stringToArray } from "../../../utils/array";
import { countParPageSerialNumber, percentage } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import {
  formatDate,
  formatDateNotNew,
  formatTimeNotNew,
  previousDate,
} from "../../../utils/time";
import ConfirmModal from "../../common/ConfirmModal";
import CustomTable from "../../common/CustomTable";
import Feedback from "./Feedback";
import ImportExcel from "./ImportExcel";
import PGDisabledModal from "./PGDisabledModal";
import PGEnableModal from "./PGEnableModal";
import TicketGenerate from "./TicketGenerate";
import View from "./View";
import ViewLog from "./ViewLog";

type Props = {
  keyword?: string;
  siteCode?: string;
  status: string;
  isVendor?: boolean;
  openImport: ModalType;
  regions?: any;
  setRegionId?: any;
  setAreaId?: any;
  setTerritoryId?: any;
  toggleImport: () => void;
  handleEnterValue2?: (val: any) => void;
  handleInputChange2?: (val: any) => void;
  filter?: {
    regionId?: string;
    areaId?: string;
    territoryId?: string;
    supplierId?: string;
    date?: any;
    today?: boolean;
  };
  downloadExcel: (data: any[]) => void;
  handleExcelLoading: (val: boolean) => void;
  downloadClicked: boolean;
  setDownloadClicked: (val: boolean) => void;
} & CustomTableProps;

export default function NetworkTable({
  status,
  keyword = "",
  siteCode = "",
  regions,
  page,
  limit,
  isFirstPage,
  handlePagination,
  handleSetFirstPage,
  openImport,
  toggleImport,
  isVendor,
  filter,
  downloadExcel,
  handleExcelLoading,
  downloadClicked,
  setDownloadClicked,
  handleEnterValue2,
  handleInputChange2,
  setRegionId,
  setAreaId,
  setTerritoryId,
}: Props) {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { open, toggle, handleOpen } = useModal();
  const {
    open: confirmOpen,
    toggle: confirmToggle,
    handleOpen: handleConfirmOpen,
  } = useModal();
  const [uploadCount, setUploadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const location = useLocation();

  const path = stringToArray(location.pathname, "/")?.[0];
  const { login } = useAppSelector((state) => state.login);
  const { loading, allTowers, towersCount, postLoading, updateLoading } =
    useAppSelector((state) => state.network);

  const pgStatus = status ? stringToArray(status, "-") : "";

  const uploadedCount = (value: number, total: number) => {
    setUploadCount(value);
    setTotalCount(total);
  };

  const menuSiteId = (
    <div className="p-2">
      <Search
        placeholder="Search by Site ID"
        onSearch={(val) => handleEnterValue2?.(val)}
        onChange={(e) => handleInputChange2?.(e)}
        // value={siteCode}
        // className="custom-search-input"
        allowClear
      />
    </div>
  );

  const menuCluster = (
    <div className="p-2">
      <Select
        value={filter?.regionId}
        placeholder="Select cluster"
        allowClear
        showSearch
        filterOption={selectSearchOption}
        options={ArrayOption(regions, "regionName", "_id", true)}
        onChange={(val) => {
          setRegionId(val);
          setAreaId(undefined);
          setTerritoryId(undefined);
        }}
        className="min-w-[120px]"
        disabled={login?.role === "region" || login?.role === "finance"}
      />
    </div>
  );

  const columns: ColumnsType<TableData<GetAllTowerData>> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: (
        <div className="flex items-center justify-center gap-3">
          <span>Site ID</span>
          {(login?.role === "edatco" || login?.role === "region") && (
            <span className="cursor-pointer mt-2">
              <Dropdown overlay={menuSiteId} trigger={["hover"]}>
                <span>
                  <FilterIcon />
                </span>
              </Dropdown>
            </span>
          )}
        </div>
      ),
      align: "center",
      dataIndex: "siteCode",
      render: (val) => {
        return (
          <>
            <div>{val ? val : "N/A"}</div>
          </>
        );
      },
    },
    {
      title: "Type",
      align: "center",
      dataIndex: "towerType",
      render: (val) => {
        let displayText = "N/A";

        if (val === "s") displayText = "Standard";
        else if (val === "p") displayText = "Premium";

        return <div>{displayText}</div>;
      },
    },

    {
      title: "Tower Name",
      align: "center",
      dataIndex: "towerName",
      render: (val) => {
        return (
          <>
            <div>{val ? capitalize(val) : "N/A"}</div>
          </>
        );
      },
    },

    {
      title: "EASI ID",
      align: "center",
      dataIndex: "easiCode",
      render: (val) => {
        return (
          <>
            <div>{val ? val : "N/A"}</div>
          </>
        );
      },
    },

    {
      title: "Robi ID",
      align: "center",
      dataIndex: "robiId",
      render: (val) => {
        return (
          <>
            <div>{val ? val : "N/A"}</div>
          </>
        );
      },
    },
    {
      title: "GP ID",
      align: "center",
      dataIndex: "gpId",
      render: (val) => {
        return (
          <>
            <div>{val ? val : "N/A"}</div>
          </>
        );
      },
    },

    {
      title: "PG Runner Name",
      dataIndex: "PgData",
      render: (val) =>
        val?.firstName ? (
          <>
            {val?.firstName} {val?.lastName}
          </>
        ) : (
          "N/A"
        ),
    },

    {
      title: (
        <div className="flex items-center justify-center gap-3">
          <span>Cluster</span>
          <span className="cursor-pointer mt-2">
            <Dropdown overlay={menuCluster} trigger={["hover"]}>
              <span>
                <FilterIcon />
              </span>
            </Dropdown>
          </span>
        </div>
      ),
      align: "center",
      dataIndex: "regionName",
      render: (val: string) => {
        const capitalizeText = (text: string) =>
          text?.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

        return <div>{capitalizeText(val)}</div>;
      },
    },

    // {
    //   title: "Region",
    //   dataIndex: "areaName",
    //   align: "center",
    // },
    {
      title: "Zone",
      dataIndex: "areaName",
      align: "center",
      render: (val: string) => {
        const capitalizeText = (text: string) =>
          text?.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

        return <div>{capitalizeText(val)}</div>;
      },
    },

    {
      title: "MP",
      dataIndex: "teritoryName",
      align: "center",
      render: (val: string) => {
        const capitalizeText = (text: string) =>
          text?.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

        return <div>{capitalizeText(val)}</div>;
      },
    },

    {
      title: "MF Time (GP)",
      align: "center",
      render: (val) => {
        const isTrue =
          (val?.latestAlarmLog?.operator?.toLowerCase() === "gp" ||
            val?.latestAlarmLog?.operator?.toLowerCase() === "both") &&
          val?.latestAlarmLog?.mFStatus &&
          val?.latestAlarmLog?.mFOccuredTime;

        const isTrue2 =
          (val?.towerTicketLogData?.operator?.toLowerCase() === "gp" ||
            val?.towerTicketLogData?.operator?.toLowerCase() === "both") &&
          val?.towerTicketLogData?.mFStatus &&
          val?.towerTicketLogData?.mFOccuredTime;

        const showLatest = isTrue && val?.latestAlarmLog?.mFOccuredTime;
        const showTower = isTrue2 && val?.towerTicketLogData?.mFOccuredTime;

        return (
          <div>
            {showLatest
              ? `${formatDate(
                  val.latestAlarmLog.mFOccuredTime
                )} - ${formatTimeNotNew(
                  val.latestAlarmLog.mFOccuredTime,
                  "h:mm A"
                )}`
              : showTower
              ? `${formatDate(
                  val.towerTicketLogData.mFOccuredTime
                )} - ${formatTimeNotNew(
                  val.towerTicketLogData.mFOccuredTime,
                  "h:mm A"
                )}`
              : "False"}
          </div>
        );
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableSky",
        };
      },
    },
    {
      title: (
        <div className="flex items-center justify-center gap-3">
          <span>MF Duration (GP)</span>
          {/* <span className="cursor-pointer mt-2">
            <FilterIcon />
          </span> */}
        </div>
      ),
      align: "center",
      render: (val) => {
        const isTrue =
          (val?.latestAlarmLog?.operator?.toLowerCase() === "gp" ||
            val?.latestAlarmLog?.operator?.toLowerCase() === "both") &&
          val?.latestAlarmLog?.mFStatus &&
          val?.latestAlarmLog?.mFOccuredTime;

        const isTrue2 =
          (val?.towerTicketLogData?.operator?.toLowerCase() === "gp" ||
            val?.towerTicketLogData?.operator?.toLowerCase() === "both") &&
          val?.towerTicketLogData?.mFStatus &&
          val?.towerTicketLogData?.mFOccuredTime;

        if (!isTrue && !isTrue2) {
          return "N/A";
        }

        const occurredTimeLatest = isTrue
          ? new Date(val?.latestAlarmLog?.mFOccuredTime)
          : null;

        const occurredTimeTower = isTrue2
          ? new Date(val?.towerTicketLogData?.mFOccuredTime)
          : null;

        // Priority selection
        let selectedTime = null;
        if (occurredTimeLatest) {
          selectedTime = occurredTimeLatest;
        } else if (occurredTimeTower) {
          selectedTime = occurredTimeTower;
        } else {
          return "N/A";
        }

        const now = new Date();
        const diff = now.getTime() - selectedTime.getTime();

        if (diff < 0) return "N/A";

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        let parts = [];
        if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
        if (hours > 0) parts.push(`${hours} hr${hours > 1 ? "s" : ""}`);
        if (minutes > 0 || parts.length === 0)
          parts.push(`${minutes} min${minutes > 1 ? "s" : ""}`);

        return parts.join(" ");
      },

      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableSky",
        };
      },
    },
    {
      title: "DC Low Time (GP)",
      align: "center",
      render: (val) => {
        const isTrue =
          (val?.latestAlarmLog?.operator?.toLowerCase() === "gp" ||
            val?.latestAlarmLog?.operator?.toLowerCase() === "both") &&
          val?.latestAlarmLog?.dcStatus &&
          val?.latestAlarmLog?.dcOccuredTime;

        const isTrue2 =
          (val?.towerTicketLogData?.operator?.toLowerCase() === "gp" ||
            val?.towerTicketLogData?.operator?.toLowerCase() === "both") &&
          val?.towerTicketLogData?.dcStatus &&
          val?.towerTicketLogData?.dcOccuredTime;

        const showLatest = isTrue && val?.latestAlarmLog?.dcOccuredTime;
        const showTower = isTrue2 && val?.towerTicketLogData?.dcOccuredTime;

        return (
          <div>
            {showLatest
              ? `${formatDate(
                  val?.latestAlarmLog?.dcOccuredTime
                )} - ${formatTimeNotNew(
                  val?.latestAlarmLog?.dcOccuredTime,
                  "h:mm A"
                )}`
              : showTower
              ? `${formatDate(
                  val?.towerTicketLogData?.dcOccuredTime
                )} - ${formatTimeNotNew(
                  val?.towerTicketLogData?.dcOccuredTime,
                  "h:mm A"
                )}`
              : "False"}
          </div>
        );
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableSky",
        };
      },
    },

    {
      title: (
        <div className="flex items-center justify-center gap-3">
          <span>DC Low Duration (GP)</span>
          {/* <span className="cursor-pointer mt-2">
            <FilterIcon />
          </span> */}
        </div>
      ),
      align: "center",
      render: (val) => {
        const isTrue =
          (val?.latestAlarmLog?.operator?.toLowerCase() === "gp" ||
            val?.latestAlarmLog?.operator?.toLowerCase() === "both") &&
          val?.latestAlarmLog?.dcStatus &&
          val?.latestAlarmLog?.dcOccuredTime;

        const isTrue2 =
          (val?.towerTicketLogData?.operator?.toLowerCase() === "gp" ||
            val?.towerTicketLogData?.operator?.toLowerCase() === "both") &&
          val?.towerTicketLogData?.dcStatus &&
          val?.towerTicketLogData?.dcOccuredTime;

        if (!isTrue && !isTrue2) {
          return "N/A";
        }

        const occurredTimeLatest = isTrue
          ? new Date(val?.latestAlarmLog?.dcOccuredTime)
          : null;

        const occurredTimeTower = isTrue2
          ? new Date(val?.towerTicketLogData?.dcOccuredTime)
          : null;

        // Priority selection
        let selectedTime = null;
        if (occurredTimeLatest) {
          selectedTime = occurredTimeLatest;
        } else if (occurredTimeTower) {
          selectedTime = occurredTimeTower;
        } else {
          return "N/A";
        }

        const now = new Date();
        const diff = now.getTime() - selectedTime.getTime();

        if (diff < 0) return "N/A";

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        let parts = [];
        if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
        if (hours > 0) parts.push(`${hours} hr${hours > 1 ? "s" : ""}`);
        if (minutes > 0 || parts.length === 0)
          parts.push(`${minutes} min${minutes > 1 ? "s" : ""}`);

        return parts.join(" ");
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableSky",
        };
      },
    },

    {
      title: "RMS Voltage (GP)",
      align: "center",
      render: (val) => {
        const isTrue =
          (val?.latestAlarmLog?.operator?.toLowerCase() === "gp" ||
            val?.latestAlarmLog?.operator?.toLowerCase() === "both") &&
          val?.latestAlarmLog?.siteDcVoltage;

        const isTrue2 =
          (val?.towerTicketLogData?.operator?.toLowerCase() === "gp" ||
            val?.towerTicketLogData?.operator?.toLowerCase() === "both") &&
          val?.towerTicketLogData?.siteDcVoltage;

        const showLatest = isTrue;
        const showTower = isTrue2;

        return (
          <div>
            {showLatest
              ? val?.latestAlarmLog?.siteDcVoltage
              : showTower
              ? val?.towerTicketLogData?.siteDcVoltage
              : "N/A"}
          </div>
        );
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableSky",
        };
      },
    },

    {
      title: "SD Time (GP)",
      align: "center",
      render: (val, record: any) => {
        const latestTime = val?.latestAlarmLog?.sdOccuredTime;
        const towerTime = val?.towerTicketLogData?.sdOccuredTime;

        const isTrue =
          (val?.latestAlarmLog?.operator?.toLowerCase() === "gp" ||
            val?.latestAlarmLog?.operator?.toLowerCase() === "both") &&
          val?.latestAlarmLog?.sdStatus &&
          latestTime &&
          latestTime !== "N/A";

        const isTrue2 =
          (val?.towerTicketLogData?.operator?.toLowerCase() === "gp" ||
            val?.towerTicketLogData?.operator?.toLowerCase() === "both") &&
          val?.towerTicketLogData?.sdStatus &&
          towerTime &&
          towerTime !== "N/A";

        const showLatest = isTrue && latestTime;
        const showTower = isTrue2 && towerTime;

        return (
          <div>
            {showLatest
              ? `${formatDate(latestTime)} - ${formatTimeNotNew(
                  latestTime,
                  "h:mm A"
                )}`
              : showTower
              ? `${formatDate(towerTime)} - ${formatTimeNotNew(
                  towerTime,
                  "h:mm A"
                )}`
              : "False"}
          </div>
        );
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableSky",
        };
      },
    },

    {
      title: (
        <div className="flex items-center justify-center gap-3">
          <span>SD Duration (GP)</span>
          {/* <span className="cursor-pointer mt-2">
            <FilterIcon />
          </span> */}
        </div>
      ),
      align: "center",
      render: (val, record: any) => {
        // If any of the times is literally "N/A" string → show N/A
        if (
          val?.latestAlarmLog?.sdOccuredTime === "N/A" ||
          val?.towerTicketLogData?.sdOccuredTime === "N/A"
        ) {
          return "N/A";
        }

        const isTrue =
          (val?.latestAlarmLog?.operator?.toLowerCase() === "gp" ||
            val?.latestAlarmLog?.operator?.toLowerCase() === "both") &&
          val?.latestAlarmLog?.sdStatus &&
          val?.latestAlarmLog?.sdOccuredTime;

        const isTrue2 =
          (val?.towerTicketLogData?.operator?.toLowerCase() === "gp" ||
            val?.towerTicketLogData?.operator?.toLowerCase() === "both") &&
          val?.towerTicketLogData?.sdStatus &&
          val?.towerTicketLogData?.sdOccuredTime;

        if (!isTrue && !isTrue2) {
          return "N/A";
        }

        const occurredTimeLatest = isTrue
          ? new Date(val?.latestAlarmLog?.sdOccuredTime)
          : null;

        const occurredTimeTower = isTrue2
          ? new Date(val?.towerTicketLogData?.sdOccuredTime)
          : null;

        // Priority selection
        let selectedTime = null;
        if (occurredTimeLatest) {
          selectedTime = occurredTimeLatest;
        } else if (occurredTimeTower) {
          selectedTime = occurredTimeTower;
        } else {
          return "N/A";
        }

        const now = new Date();
        const diff = now.getTime() - selectedTime.getTime();

        if (diff < 0) return "N/A";

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        let parts = [];
        if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
        if (hours > 0) parts.push(`${hours} hr${hours > 1 ? "s" : ""}`);
        if (minutes > 0 || parts.length === 0)
          parts.push(`${minutes} min${minutes > 1 ? "s" : ""}`);

        return parts.join(" ");
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableSky",
        };
      },
    },

    {
      title: "Phase Alarm (GP)",
      align: "center",
      render: (val, record: any) => {
        const latestTime = val?.latestAlarmLog?.phaseAlarm;
        const towerTime = val?.towerTicketLogData?.phaseAlarm;

        const isTrue =
          (val?.latestAlarmLog?.operator?.toLowerCase() === "gp" ||
            val?.latestAlarmLog?.operator?.toLowerCase() === "both") &&
          latestTime &&
          latestTime !== "N/A";

        const isTrue2 =
          (val?.towerTicketLogData?.operator?.toLowerCase() === "gp" ||
            val?.towerTicketLogData?.operator?.toLowerCase() === "both") &&
          towerTime &&
          towerTime !== "N/A";

        const showLatest = isTrue && latestTime;
        const showTower = isTrue2 && towerTime;

        return (
          <div>
            {showLatest
              ? val?.latestAlarmLog?.phaseAlarm === true
                ? "True"
                : "False"
              : showTower
              ? val?.towerTicketLogData?.phaseAlarm === true
                ? "True"
                : "False"
              : "N/A"}
          </div>
        );
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableSky",
        };
      },
    },

    {
      title: "MF Time (Robi)",
      align: "center",
      render: (val) => {
        const isTrue =
          (val?.latestAlarmLog?.operator?.toLowerCase() === "robi" ||
            val?.latestAlarmLog?.operator?.toLowerCase() === "both") &&
          val?.latestAlarmLog?.mFStatus &&
          val?.latestAlarmLog?.mFOccuredTime;

        const isTrue2 =
          (val?.towerTicketLogData?.operator?.toLowerCase() === "robi" ||
            val?.towerTicketLogData?.operator?.toLowerCase() === "both") &&
          val?.towerTicketLogData?.mFStatus &&
          val?.towerTicketLogData?.mFOccuredTime;

        const showLatest = isTrue && val?.latestAlarmLog?.mFOccuredTime;
        const showTower = isTrue2 && val?.towerTicketLogData?.mFOccuredTime;

        return (
          <div>
            {showLatest
              ? `${formatDate(
                  val.latestAlarmLog.mFOccuredTime
                )} - ${formatTimeNotNew(
                  val.latestAlarmLog.mFOccuredTime,
                  "h:mm A"
                )}`
              : showTower
              ? `${formatDate(
                  val.towerTicketLogData.mFOccuredTime
                )} - ${formatTimeNotNew(
                  val.towerTicketLogData.mFOccuredTime,
                  "h:mm A"
                )}`
              : "False"}
          </div>
        );
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableRed",
        };
      },
    },

    {
      title: (
        <div className="flex items-center justify-center gap-3">
          <span>MF Duration (Robi)</span>
          {/* <span className="cursor-pointer mt-2">
            <FilterIcon />
          </span> */}
        </div>
      ),
      align: "center",
      render: (val) => {
        const isTrue =
          (val?.latestAlarmLog?.operator?.toLowerCase() === "robi" ||
            val?.latestAlarmLog?.operator?.toLowerCase() === "both") &&
          val?.latestAlarmLog?.mFStatus &&
          val?.latestAlarmLog?.mFOccuredTime;

        const isTrue2 =
          (val?.towerTicketLogData?.operator?.toLowerCase() === "robi" ||
            val?.towerTicketLogData?.operator?.toLowerCase() === "both") &&
          val?.towerTicketLogData?.mFStatus &&
          val?.towerTicketLogData?.mFOccuredTime;

        if (!isTrue && !isTrue2) {
          return "N/A";
        }

        const occurredTimeLatest = isTrue
          ? new Date(val?.latestAlarmLog?.mFOccuredTime)
          : null;

        const occurredTimeTower = isTrue2
          ? new Date(val?.towerTicketLogData?.mFOccuredTime)
          : null;

        // Priority selection
        let selectedTime = null;
        if (occurredTimeLatest) {
          selectedTime = occurredTimeLatest;
        } else if (occurredTimeTower) {
          selectedTime = occurredTimeTower;
        } else {
          return "N/A";
        }

        const now = new Date();
        const diff = now.getTime() - selectedTime.getTime();

        if (diff < 0) return "N/A";

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        let parts = [];
        if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
        if (hours > 0) parts.push(`${hours} hr${hours > 1 ? "s" : ""}`);
        if (minutes > 0 || parts.length === 0)
          parts.push(`${minutes} min${minutes > 1 ? "s" : ""}`);

        return parts.join(" ");
      },
      onHeaderCell: () => ({
        className: "custom_edotco_tableRed",
      }),
    },
    {
      title: "DC Low Time (Robi)",
      align: "center",
      render: (val) => {
        const isTrue =
          (val?.latestAlarmLog?.operator?.toLowerCase() === "robi" ||
            val?.latestAlarmLog?.operator?.toLowerCase() === "both") &&
          val?.latestAlarmLog?.dcStatus &&
          val?.latestAlarmLog?.dcOccuredTime;

        const isTrue2 =
          (val?.towerTicketLogData?.operator?.toLowerCase() === "robi" ||
            val?.towerTicketLogData?.operator?.toLowerCase() === "both") &&
          val?.towerTicketLogData?.dcStatus &&
          val?.towerTicketLogData?.dcOccuredTime;

        const showLatest = isTrue && val?.latestAlarmLog?.dcOccuredTime;
        const showTower = isTrue2 && val?.towerTicketLogData?.dcOccuredTime;

        return (
          <div>
            {showLatest
              ? `${formatDate(
                  val?.latestAlarmLog?.dcOccuredTime
                )} - ${formatTimeNotNew(
                  val?.latestAlarmLog?.dcOccuredTime,
                  "h:mm A"
                )}`
              : showTower
              ? `${formatDate(
                  val?.towerTicketLogData?.dcOccuredTime
                )} - ${formatTimeNotNew(
                  val?.towerTicketLogData?.dcOccuredTime,
                  "h:mm A"
                )}`
              : "False"}
          </div>
        );
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableRed",
        };
      },
    },

    {
      title: (
        <div className="flex items-center justify-center gap-3">
          <span>DC Low Duration (Robi)</span>
          {/* <span className="cursor-pointer mt-2">
            <FilterIcon />
          </span> */}
        </div>
      ),
      align: "center",
      render: (val) => {
        const isTrue =
          (val?.latestAlarmLog?.operator?.toLowerCase() === "robi" ||
            val?.latestAlarmLog?.operator?.toLowerCase() === "both") &&
          val?.latestAlarmLog?.dcStatus &&
          val?.latestAlarmLog?.dcOccuredTime;

        const isTrue2 =
          (val?.towerTicketLogData?.operator?.toLowerCase() === "robi" ||
            val?.towerTicketLogData?.operator?.toLowerCase() === "both") &&
          val?.towerTicketLogData?.dcStatus &&
          val?.towerTicketLogData?.dcOccuredTime;

        if (!isTrue && !isTrue2) {
          return "N/A";
        }

        const occurredTimeLatest = isTrue
          ? new Date(val?.latestAlarmLog?.dcOccuredTime)
          : null;

        const occurredTimeTower = isTrue2
          ? new Date(val?.towerTicketLogData?.dcOccuredTime)
          : null;

        // Priority selection
        let selectedTime = null;
        if (occurredTimeLatest) {
          selectedTime = occurredTimeLatest;
        } else if (occurredTimeTower) {
          selectedTime = occurredTimeTower;
        } else {
          return "N/A";
        }

        const now = new Date();
        const diff = now.getTime() - selectedTime.getTime();

        if (diff < 0) return "N/A";

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        let parts = [];
        if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
        if (hours > 0) parts.push(`${hours} hr${hours > 1 ? "s" : ""}`);
        if (minutes > 0 || parts.length === 0)
          parts.push(`${minutes} min${minutes > 1 ? "s" : ""}`);

        return parts.join(" ");
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableRed",
        };
      },
    },

    {
      title: "RMS Voltage (Robi)",
      align: "center",
      render: (val) => {
        const isTrue =
          (val?.latestAlarmLog?.operator?.toLowerCase() === "robi" ||
            val?.latestAlarmLog?.operator?.toLowerCase() === "both") &&
          val?.latestAlarmLog?.siteDcVoltage;

        const isTrue2 =
          (val?.towerTicketLogData?.operator?.toLowerCase() === "robi" ||
            val?.towerTicketLogData?.operator?.toLowerCase() === "both") &&
          val?.towerTicketLogData?.siteDcVoltage;

        const showLatest = isTrue;
        const showTower = isTrue2;

        return (
          <div>
            {showLatest
              ? val?.latestAlarmLog?.siteDcVoltage
              : showTower
              ? val?.towerTicketLogData?.siteDcVoltage
              : "N/A"}
          </div>
        );
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableRed",
        };
      },
    },
    {
      title: "SD Time (Robi)",
      align: "center",
      render: (val, record: any) => {
        const latestTime = val?.latestAlarmLog?.sdOccuredTime;
        const towerTime = val?.towerTicketLogData?.sdOccuredTime;

        const isTrue =
          (val?.latestAlarmLog?.operator?.toLowerCase() === "robi" ||
            val?.latestAlarmLog?.operator?.toLowerCase() === "both") &&
          val?.latestAlarmLog?.sdStatus &&
          latestTime &&
          latestTime !== "N/A";

        const isTrue2 =
          (val?.towerTicketLogData?.operator?.toLowerCase() === "robi" ||
            val?.towerTicketLogData?.operator?.toLowerCase() === "both") &&
          val?.towerTicketLogData?.sdStatus &&
          towerTime &&
          towerTime !== "N/A";

        const showLatest = isTrue && latestTime;
        const showTower = isTrue2 && towerTime;

        return (
          <div>
            {showLatest
              ? `${formatDate(latestTime)} - ${formatTimeNotNew(
                  latestTime,
                  "h:mm A"
                )}`
              : showTower
              ? `${formatDate(towerTime)} - ${formatTimeNotNew(
                  towerTime,
                  "h:mm A"
                )}`
              : "False"}
          </div>
        );
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableRed",
        };
      },
    },

    {
      title: (
        <div className="flex items-center justify-center gap-3">
          <span>SD Duration (Robi)</span>
          {/* <span className="cursor-pointer mt-2">
            <FilterIcon />
          </span> */}
        </div>
      ),
      align: "center",
      render: (val, record: any) => {
        // If any of the times is literally "N/A" string → show N/A
        if (
          val?.latestAlarmLog?.sdOccuredTime === "N/A" ||
          val?.towerTicketLogData?.sdOccuredTime === "N/A"
        ) {
          return "N/A";
        }

        const isTrue =
          (val?.latestAlarmLog?.operator?.toLowerCase() === "robi" ||
            val?.latestAlarmLog?.operator?.toLowerCase() === "both") &&
          val?.latestAlarmLog?.sdStatus &&
          val?.latestAlarmLog?.sdOccuredTime;

        const isTrue2 =
          (val?.towerTicketLogData?.operator?.toLowerCase() === "robi" ||
            val?.towerTicketLogData?.operator?.toLowerCase() === "both") &&
          val?.towerTicketLogData?.sdStatus &&
          val?.towerTicketLogData?.sdOccuredTime;

        if (!isTrue && !isTrue2) {
          return "N/A";
        }

        const occurredTimeLatest = isTrue
          ? new Date(val?.latestAlarmLog?.sdOccuredTime)
          : null;

        const occurredTimeTower = isTrue2
          ? new Date(val?.towerTicketLogData?.sdOccuredTime)
          : null;

        // Priority selection
        let selectedTime = null;
        if (occurredTimeLatest) {
          selectedTime = occurredTimeLatest;
        } else if (occurredTimeTower) {
          selectedTime = occurredTimeTower;
        } else {
          return "N/A";
        }

        const now = new Date();
        const diff = now.getTime() - selectedTime.getTime();

        if (diff < 0) return "N/A";

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        let parts = [];
        if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
        if (hours > 0) parts.push(`${hours} hr${hours > 1 ? "s" : ""}`);
        if (minutes > 0 || parts.length === 0)
          parts.push(`${minutes} min${minutes > 1 ? "s" : ""}`);

        return parts.join(" ");
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableRed",
        };
      },
    },

    {
      title: "Phase Alarm (Robi)",
      align: "center",
      render: (val, record: any) => {
        const latestTime = val?.latestAlarmLog?.phaseAlarm;
        const towerTime = val?.towerTicketLogData?.phaseAlarm;

        const isTrue =
          (val?.latestAlarmLog?.operator?.toLowerCase() === "robi" ||
            val?.latestAlarmLog?.operator?.toLowerCase() === "both") &&
          latestTime &&
          latestTime !== "N/A";

        const isTrue2 =
          (val?.towerTicketLogData?.operator?.toLowerCase() === "robi" ||
            val?.towerTicketLogData?.operator?.toLowerCase() === "both") &&
          towerTime &&
          towerTime !== "N/A";

        const showLatest = isTrue && latestTime;
        const showTower = isTrue2 && towerTime;

        return (
          <div>
            {showLatest
              ? val?.latestAlarmLog?.phaseAlarm === true
                ? "True"
                : "False"
              : showTower
              ? val?.towerTicketLogData?.phaseAlarm === true
                ? "True"
                : "False"
              : "N/A"}
          </div>
        );
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableRed",
        };
      },
    },

    {
      title: "TT No.",
      dataIndex: "numberOfOperation",
      align: "center",
      render: (val) => val || 0,
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableGreen",
        };
      },
    },
    {
      title: "TT Generation Time",
      dataIndex: "towerTicketLastData",
      align: "center",
      render: (val) => {
        const ttGenerateTime = val?.createdAt;

        const formattedTTGenerateDate =
          formatDateNotNew(ttGenerateTime) ?? "N/A";

        const formattedTTGenerateTime =
          formatTimeNotNew(ttGenerateTime, "h:mm A") ?? "N/A";

        return (
          <>
            <span>{`${formattedTTGenerateDate} - ${formattedTTGenerateTime}`}</span>
          </>
        );
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableGreen",
        };
      },
    },

    // {
    //   title: "Approx. Reach Time",
    //   render: () => {
    //     return <div>7/2/2025 9:54:12 PM</div>;
    //   },
    //   onHeaderCell: () => {
    //     return {
    //       className: "custom_edotco_tableGreen",
    //     };
    //   },
    // },

    {
      title: "TT Status",
      align: "center",
      render: (val) => (
        <span
          className={`status ${
            val?.towerTicketLastData?.status === "pending"
              ? "status-pending"
              : val?.towerTicketLastData?.status === "accepted"
              ? "status-done"
              : val?.towerTicketLastData?.status === "notaccepted"
              ? "status-waiting"
              : val?.towerTicketLastData?.status === "cancel"
              ? "status-danger"
              : null
          }`}
        >
          {!val?.towerTicketLastData?.status ||
          val?.towerTicketLastData?.status?.toLowerCase() === "n/a"
            ? "N/A"
            : capitalize(val?.towerTicketLastData?.status)}
        </span>
      ),
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableGreen",
        };
      },
    },

    {
      title: "PG Status",
      align: "center",
      render: (val: GetAllTowerData) => (
        <span
          className={`status ${
            val?.towerTicketLogData?.status === "pending"
              ? "status-pending"
              : val?.towerTicketLogData?.missCall
              ? "status-danger"
              : val?.towerTicketLogData?.status === "reached"
              ? "status-waiting"
              : val?.towerTicketLogData?.status === "finished"
              ? "status-done"
              : val?.towerTicketLogData?.status === "start"
              ? "status-info"
              : null
          }`}
        >
          {val?.towerTicketLogData?.missCall
            ? "Missed Call"
            : val?.towerTicketLogData?.status
            ? capitalize(val?.towerTicketLogData?.status)
            : "N/A"}
        </span>
      ),
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableGreen",
        };
      },
    },
    {
      title: "Details",
      align: "center",
      render: (val: GetAllTowerData) => (
        <button
          className={`status border-none flex items-center gap-2 p-1 !bg-[#F2F5FF] !text-[#4A72FF] cursor-pointer ${
            val?.towerTicketLogData?._id
              ? "status-info cursor-pointer"
              : "status-info !text-gray-400"
          }`}
          onClick={() =>
            handleOpen({
              type: "LOG",
              data: val,
            })
          }
          disabled={!val?.towerTicketLogData?._id}
        >
          <EyeIcon color="#4A72FF" />
          View
        </button>
      ),
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableGreen",
        };
      },
    },
    {
      title: "Operator",
      align: "center",
      render: (val) => {
        const showLatest = val?.towerTicketLastData?.operator?.toLowerCase();
        const showTower = val?.towerTicketLogData?.operator?.toLowerCase();
        return (
          <div>
            {showLatest
              ? showLatest === "gp"
                ? "GP"
                : capitalize(val?.towerTicketLastData?.operator)
              : showTower
              ? showTower === "gp"
                ? "GP"
                : capitalize(val?.towerTicketLogData?.operator)
              : "N/A"}
          </div>
        );
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableGreen",
        };
      },
    },

    // {
    //   title: "Site Locator Name",
    //   render: (val: GetAllTowerData) => {
    //     return (
    //       <>
    //         <div className="text-slate-600 text-base font-medium">
    //           {val?.siteCode}
    //         </div>
    //         <div className="text-slate-500 text-xs font-normal">
    //           Tower name: {capitalize(val?.towerName)}
    //         </div>
    //       </>
    //     );
    //   },
    // },
    // {
    //   title: "PG Name",
    //   render: (val: GetAllTowerData) => {
    //     return (
    //       <>
    //         <div>Abir Hossen</div>
    //       </>
    //     );
    //   },
    // },
    // {
    //   title: "Structure Type",
    //   dataIndex: "structureType",
    //   align: "center",
    // },
    // {
    //   title: "Location",
    //   align: "center",
    //   render: (val: NetworkTableColumn) => (
    //     <span
    //       className={`${
    //         val?.location === "Indoor"
    //           ? "text-status-indoor"
    //           : "text-status-outdoor"
    //       }`}
    //     >
    //       {val?.location || "-"}
    //     </span>
    //   ),
    // },
    // {
    //   title: "Cluster",
    //   dataIndex: "regionName",
    //   align: "center",
    // },
    // {
    //   title: "Zone",
    //   dataIndex: "areaName",
    //   align: "center",
    // },
    // {
    //   title: "MP",
    //   dataIndex: "teritoryName",
    //   align: "center",
    // },
    // {
    //   title: "Grid fail time",
    //   dataIndex: "towerTicketLogData",
    //   align: "center",
    //   render: (val) => {
    //     const startTime = val?.pgAlert?.startTime?.time;
    //     const stopTime = val?.pgAlert?.stopTime?.time;

    //     // Convert the time format using moment
    //     const formattedStartTime = startTime
    //       ? moment(startTime, "hh:mm A").format("h:mm A")
    //       : "N/A";
    //     const formattedStopTime = stopTime
    //       ? moment(stopTime, "hh:mm A").format("h:mm A")
    //       : "N/A";

    //     return (
    //       <>
    //         <span>{formattedStartTime}</span>
    //         <span> - </span>
    //         <span>{formattedStopTime}</span>
    //       </>
    //     );
    //   },
    // },
    // {
    //   title: "Site Status",
    //   align: "center",
    //   render: (val: GetAllTowerData) => (
    //     <span
    //       className={`status ${
    //         val?.towerTicketLastData?.status === "pending"
    //           ? "status-pending"
    //           : val?.towerTicketLastData?.status === "accepted"
    //           ? "status-done"
    //           : val?.towerTicketLastData?.status === "notaccepted"
    //           ? "status-waiting"
    //           : val?.towerTicketLastData?.status === "cancel"
    //           ? "status-danger"
    //           : null
    //       }`}
    //     >
    //       {val?.towerTicketLastData?.status
    //         ? capitalize(val?.towerTicketLastData?.status)
    //         : "N/A"}
    //     </span>
    //   ),
    // },

    // {
    //   title: "PG Connect Time",
    //   dataIndex: "towerTicketLogData",
    //   align: "center",
    //   render: (val) => {
    //     const startTime = val?.pgStart?.time;

    //     // Convert the time format using moment
    //     const formattedStartTime = startTime
    //       ? moment(startTime, "hh:mm A").format("h:mm A")
    //       : "N/A";

    //     return (
    //       <>
    //         <span>{formattedStartTime}</span>
    //       </>
    //     );
    //   },
    // },

    // {
    //   title: "Tenant Name",
    //   dataIndex: "contactPersonName",
    //   align: "center",
    //   render: (val) => val || "N/A",
    // },
    // {
    //   title: "TT No",
    //   dataIndex: "numberOfOperation",
    //   align: "center",
    //   render: (val) => val || 0,
    // },

    // {
    //   title: "TT Generate Time",
    //   dataIndex: "towerTicketLastData",
    //   align: "center",
    //   render: (val) => {
    //     const ttGenerateTime = val?.createdAt;

    //     const formattedTTGenerateDate =
    //       formatDateNotNew(ttGenerateTime) ?? "N/A";

    //     const formattedTTGenerateTime =
    //       formatTimeNotNew(ttGenerateTime, "h:mm A") ?? "N/A";

    //     return (
    //       <>
    //         <span>{`${formattedTTGenerateDate} - ${formattedTTGenerateTime}`}</span>
    //       </>
    //     );
    //   },
    // },

    // {
    //   title: "TT Status",
    //   align: "center",
    //   render: (val) => (
    //     <span
    //       className={`status ${
    //         val?.towerTicketLastData?.status === "pending"
    //           ? "status-pending"
    //           : val?.towerTicketLastData?.status === "accepted"
    //           ? "status-done"
    //           : val?.towerTicketLastData?.status === "notaccepted"
    //           ? "status-waiting"
    //           : val?.towerTicketLastData?.status === "cancel"
    //           ? "status-danger"
    //           : null
    //       }`}
    //     >
    //       {val?.towerTicketLastData?.status
    //         ? capitalize(val?.towerTicketLastData?.status)
    //         : "N/A"}
    //     </span>
    //   ),
    // },

    // {
    //   title: "PG Status",
    //   align: "center",
    //   render: (val: GetAllTowerData) => (
    //     <span
    //       className={`status ${
    //         val?.towerTicketLogData?.status === "pending"
    //           ? "status-pending"
    //           : val?.towerTicketLogData?.missCall
    //           ? "status-danger"
    //           : val?.towerTicketLogData?.status === "reached"
    //           ? "status-waiting"
    //           : val?.towerTicketLogData?.status === "finished"
    //           ? "status-done"
    //           : val?.towerTicketLogData?.status === "start"
    //           ? "status-info"
    //           : null
    //       }`}
    //     >
    //       {val?.towerTicketLogData?.missCall
    //         ? "Missed Call"
    //         : val?.towerTicketLogData?.status
    //         ? capitalize(val?.towerTicketLogData?.status)
    //         : "N/A"}
    //     </span>
    //   ),
    // },
    // {
    //   title: "Details View",
    //   align: "center",
    //   render: (val: GetAllTowerData) => (
    //     <button
    //       className={`status border-none ${
    //         val?.towerTicketLogData?._id
    //           ? "status-info cursor-pointer"
    //           : "status-info !text-gray-400"
    //       }`}
    //       onClick={() =>
    //         handleOpen({
    //           type: "LOG",
    //           data: val,
    //         })
    //       }
    //       disabled={!val?.towerTicketLogData?._id ? true : false}
    //     >
    //       View
    //     </button>
    //   ),
    // },
    // {
    //   title: "Remaining PO Balance",
    //   align: "center",
    //   render: (val) => {
    //     return (
    //       <>
    //         <div>Badda</div>
    //       </>
    //     );
    //   },
    // },
    // {
    //   title: "Usage Percentage",
    //   align: "center",
    //   render: (val) => {
    //     return (
    //       <>
    //         <div className="text-red-500">0%</div>
    //         {/* <div className="text-green-500">-25%</div>
    //         <div>45%</div> */}
    //       </>
    //     );
    //   },
    // },

    // {
    //   title: "TT Action",
    //   align: "center",
    //   render: (val: GetAllTowerData) => (
    //     <div className="flex items-center gap-1 justify-center">
    //       {val?.towerTicketLogData?.status === "finished" &&
    //       !val?.towerTicketLogData?.rating ? (
    //         <Button
    //           className="btn-status btn-status-done"
    //           onClick={() =>
    //             handleOpen({
    //               type: val?.towerTicketLogData?.status,
    //               data: val,
    //             })
    //           }
    //           disabled={val?.towerTicketLogData?.rating ? true : false}
    //         >
    //           Feedback
    //         </Button>
    //       ) : val?.towerTicketLastData?.status === "accepted" &&
    //         val?.towerTicketLogData?.status !== "finished" &&
    //         val?.towerTicketLastData?._id &&
    //         !isVendor ? (
    //         <Button
    //           className="btn-status !bg-status-info-btn"
    //           onClick={() =>
    //             handleConfirmOpen({
    //               type: "REMOVE_PG",
    //               data: val?.towerTicketLastData?._id,
    //             })
    //           }
    //         >
    //           Remove PG
    //         </Button>
    //       ) : val?.towerTicketLastData?.status === "pending" &&
    //         !val?.towerTicketLogData?.status ? (
    //         <Button
    //           className="btn-status !bg-status-waiting-btn"
    //           onClick={() =>
    //             handleConfirmOpen({
    //               type: "CANCEL_TICKET",
    //               data: val?.towerTicketLastData?._id,
    //             })
    //           }
    //         >
    //           Cancel Ticket
    //         </Button>
    //       ) : (
    //         <Button
    //           className="btn-status btn-status-danger"
    //           onClick={() =>
    //             handleOpen({
    //               type: "Inactive",
    //               data: val,
    //             })
    //           }
    //           disabled={
    //             val?.towerTicketLogData?.status === "pending" ||
    //             val?.towerTicketLogData?.status === "reached" ||
    //             val?.towerTicketLogData?.status === "start" ||
    //             val?.towerTicketLastData?.status === "pending"
    //           }
    //         >
    //           Ticket Generate
    //         </Button>
    //       )}
    //     </div>
    //   ),
    //   // render: (val: GetAllTowerData) =>
    //   //   false ? (
    //   //     <Button
    //   //       // TODO: val?.status === "Inactive"
    //   //       className="btn-status btn-status-danger"
    //   //       onClick={() =>
    //   //         handleOpen({
    //   //           // TODO: type: val?.status,
    //   //           type: "Inactive",
    //   //           data: val,
    //   //         })
    //   //       }
    //   //       disabled={val?.towerTicketLogData?.status === "pending"}
    //   //     >
    //   //       Ticket Generate
    //   //     </Button>
    //   //   ) : val?.status === "Completed" ? (
    //   //     <Button
    //   //       className="btn-status btn-status-done"
    //   //       onClick={() =>
    //   //         handleOpen({
    //   //           type: val?.status,
    //   //           data: val,
    //   //         })
    //   //       }
    //   //     >
    //   //       Feedback
    //   //     </Button>
    //   //   ) : val?.status === "Pause" ? (
    //   //     <Button className="btn-status btn-status-waiting">Fix Now</Button>
    //   //   ) : val?.status === "Ongoing" ? (
    //   //     <Button
    //   //       className="btn-status btn-status-info"
    //   //       onClick={() =>
    //   //         handleOpen({
    //   //           type: val?.status,
    //   //           data: val,
    //   //         })
    //   //       }
    //   //     >
    //   //       View
    //   //     </Button>
    //   //   ) : val?.status === "Waiting for payment" ? (
    //   //     <Button className="btn-status btn-status-pending">Pay Now</Button>
    //   //   ) : null,
    // },

    ...(login?.role !== "vendor"
      ? [
          {
            title: "TT Action",
            align: "center" as const,
            render: (val: GetAllTowerData) => (
              <div className="flex items-center gap-1 justify-center">
                {val?.towerTicketLogData?.status === "finished" &&
                !val?.towerTicketLogData?.rating ? (
                  <Button
                    className="btn-status btn-status-done"
                    onClick={() =>
                      handleOpen({
                        type: val?.towerTicketLogData?.status,
                        data: val,
                      })
                    }
                    disabled={val?.towerTicketLogData?.rating ? true : false}
                  >
                    Feedback
                  </Button>
                ) : val?.towerTicketLastData?.status === "accepted" &&
                  val?.towerTicketLogData?.status !== "finished" &&
                  val?.towerTicketLastData?._id &&
                  !isVendor ? (
                  <Button
                    className="btn-status !bg-status-info-btn"
                    onClick={() =>
                      handleConfirmOpen({
                        type: "REMOVE_PG",
                        data: val?.towerTicketLastData?._id,
                      })
                    }
                  >
                    Remove Ticket
                  </Button>
                ) : val?.towerTicketLastData?.status === "pending" &&
                  !val?.towerTicketLogData?.status ? (
                  <Button
                    className="btn-status !bg-status-waiting-btn"
                    onClick={() =>
                      handleConfirmOpen({
                        type: "CANCEL_TICKET",
                        data: val?.towerTicketLastData?._id,
                      })
                    }
                  >
                    Cancel Ticket
                  </Button>
                ) : (
                  <Button
                    className="btn-status btn-status-danger"
                    onClick={() =>
                      handleOpen({
                        type: "Inactive",
                        data: val,
                      })
                    }
                    disabled={
                      val?.towerTicketLogData?.status === "pending" ||
                      val?.towerTicketLogData?.status === "reached" ||
                      val?.towerTicketLogData?.status === "start" ||
                      val?.towerTicketLastData?.status === "pending"
                    }
                  >
                    Ticket Generate
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
    {
      title: "PG Action",
      align: "center",
      render: (val: GetAllTowerData) => {
        // console.log(
        //   "PG Enable:",
        //   val?.towerTicketLogData?.status !== "reached",
        //   val?.towerTicketLogData?.pgStart?.time ? true : false
        // );

        // console.log(
        //   "PG Disable:",
        //   val?.towerTicketLogData?.pgStart?.time,
        //   !val?.towerTicketLogData?.pgOff?.time ? false : true
        // );

        // console.log("val?.towerTicketLogData:", val?.towerTicketLogData);
        return (
          <div className="flex items-center gap-2">
            <Button
              type="primary"
              onClick={() =>
                handleConfirmOpen({
                  type: "START_TICKET",
                  data: val?.towerTicketLogData?._id,
                })
              }
              disabled={
                val?.towerTicketLogData?.status !== "reached" ||
                val?.towerTicketLogData?.pgStart?.time
                  ? true
                  : false
              }
            >
              PG Enable
            </Button>
            <Button
              onClick={() =>
                handleConfirmOpen({
                  type: "STOP_TICKET",
                  data: val?.towerTicketLogData?._id,
                })
              }
              className="btn-status !bg-status-waiting-btn disabled:!bg-status-waiting-btn/60"
              disabled={
                val?.towerTicketLogData?.pgStart?.time &&
                !val?.towerTicketLogData?.pgOff?.time
                  ? false
                  : true
              }
            >
              PG Disable
            </Button>
            {/* <Button className="btn-status bg-[#0d99ff]">Accept PG</Button> */}
          </div>
        );
      },
    },
    {
      title: "Log View",
      align: "center",
      // width: 300,
      render: (val) => {
        // console.log(val);

        return (
          <>
            <Link to={`/${path}/networks/update-tower/${val?._id}`}>
              <Button icon={<EditIcon />} />
              {/* <Button type="primary" icon={<EditFilled />} /> */}
            </Link>

            <Link
              to={`/${path}/networks/single-network/${val?._id}`}
              className="ml-4"
            >
              <Button icon={<EyeIcon />} />
            </Link>
          </>
        );
      },
    },
  ];

  // const startDate = searchParams.get("startDate");
  // const endDate = searchParams.get("endDate");

  const networkData = useCallback(() => {
    const params: any =
      keyword || siteCode
        ? { limit: 10, skip: 0 }
        : {
            limit: limit,
            skip: limit * (page - 1),
          };

    if (
      login?.role === "region" ||
      login?.role === "finance" ||
      login?.role === "vendor"
    ) {
      params.region = login?.region;
    } else {
      params.region = filter?.regionId;
    }

    params.area = filter?.areaId;
    params.teritory = filter?.territoryId;
    params.supplier = filter?.supplierId;

    if (searchParams.get("today") === "true") {
      params.today = previousDate(formatDate(new Date(), "YYYY-MM-DD"));
    } else {
      params.startDate = filter?.date?.from
        ? previousDate(filter?.date?.from)
        : "";
      // previousDate
      params.endDate = filter?.date?.to
        ? formatDate(filter?.date?.to, "YYYY-MM-DD")
        : "";
      params.today = "";
    }

    const filterData =
      status === "unrespond"
        ? { unrespond: true }
        : status === "missCall"
        ? { filter: ["finished"], missCall: true }
        : { filter: status ? [status] : [] };
    // if (keyword) {
    //   params.towerName = keyword;
    // }
    if (page) {
      params.page = page;
      params.skip = (page - 1) * limit;
    }
    dispatch(
      getAllTowerAsync({
        data: {
          ...filterData,
          towerName: keyword,
          siteCode,
        },
        others: params,
      })
    );
  }, [
    keyword,
    siteCode,
    limit,
    page,
    login?.role,
    login?.region,
    searchParams,
    status,
    dispatch,
    filter?.regionId,
    filter?.areaId,
    filter?.territoryId,
    filter?.date?.from,
    filter?.date?.to,
    filter?.supplierId,
  ]);

  const networkData2 = useCallback(() => {
    const params: any =
      keyword || siteCode
        ? { limit: 10, skip: 0 }
        : {
            limit: limit,
            skip: limit * (page - 1),
          };

    params.pg = pgStatus?.[1] || "";

    if (
      login?.role === "region" ||
      login?.role === "finance" ||
      login?.role === "vendor"
    ) {
      params.region = login?.region;
    } else {
      params.region = filter?.regionId;
    }

    params.area = filter?.areaId;
    params.teritory = filter?.territoryId;
    params.supplier = filter?.supplierId;

    if (searchParams.get("today") === "true") {
      params.today = previousDate(formatDate(new Date(), "YYYY-MM-DD"));
    } else {
      params.startDate = filter?.date?.from
        ? previousDate(filter?.date?.from)
        : "";
      // previousDate
      params.endDate = filter?.date?.to ? formatDate(filter?.date?.to) : "";
      params.today = "";
    }

    params.towerName = keyword;
    params.siteCode = siteCode;

    dispatch(
      getAllTowerAsync2({
        params,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    keyword,
    siteCode,
    limit,
    page,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    pgStatus?.[1],
    searchParams,
    filter?.regionId,
    filter?.areaId,
    filter?.territoryId,
    filter?.supplierId,
  ]);

  useEffect(() => {
    if (pgStatus?.[0] === "pg") {
      networkData2();
    } else {
      networkData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkData, networkData2, pgStatus?.[0], searchParams]);

  const getCountTower = useCallback(() => {
    const params: any =
      keyword || siteCode
        ? { limit: 1000, skip: 0 }
        : {
            limit: limit,
            skip: limit * (page - 1),
          };

    if (
      login?.role === "region" ||
      login?.role === "finance" ||
      login?.role === "vendor"
    ) {
      params.region = login?.region;
    } else {
      params.region = filter?.regionId;
    }

    params.area = filter?.areaId;
    params.teritory = filter?.territoryId;
    params.supplier = filter?.supplierId;

    if (searchParams.get("today") === "true") {
      params.today = previousDate(formatDate(new Date(), "YYYY-MM-DD"));
    } else {
      params.startDate = filter?.date?.from
        ? previousDate(filter?.date?.from)
        : "";
      params.endDate = filter?.date?.to ? previousDate(filter?.date?.to) : "";
      params.today = "";
    }

    const filterData =
      status === "unrespond"
        ? { unrespond: true }
        : status === "missCall"
        ? { filter: ["finished"], missCall: true }
        : { filter: status ? [status] : [] };
    if (keyword) {
      params.towerName = keyword;
    }
    dispatch(
      getCountTowerAsync({
        data: {
          ...filterData,
          // towerName: keyword,
          siteCode,
        },
        others: params,
      })
    );
  }, [
    keyword,
    siteCode,
    limit,
    page,
    login?.role,
    login?.region,
    searchParams,
    status,
    dispatch,
    filter?.regionId,
    filter?.areaId,
    filter?.territoryId,
    filter?.supplierId,
    filter?.date?.from,
    filter?.date?.to,
  ]);

  const getCountTower2 = useCallback(() => {
    const params: any =
      keyword || siteCode
        ? { limit: 10, skip: 0 }
        : {
            limit: limit,
            skip: limit * (page - 1),
          };

    params.pg = pgStatus?.[1] || "";

    if (
      login?.role === "region" ||
      login?.role === "finance" ||
      login?.role === "vendor"
    ) {
      params.region = login?.region;
    } else {
      params.region = filter?.regionId;
    }

    params.area = filter?.areaId;
    params.teritory = filter?.territoryId;
    params.supplier = filter?.supplierId;

    if (searchParams.get("today") === "true") {
      params.today = previousDate(formatDate(new Date(), "YYYY-MM-DD"));
    } else {
      params.startDate = filter?.date?.from
        ? previousDate(filter?.date?.from)
        : "";
      params.endDate = filter?.date?.to ? previousDate(filter?.date?.to) : "";
      params.today = "";
    }

    params.towerName = keyword;
    params.siteCode = siteCode;

    dispatch(
      getAllCountTowerAsync2({
        params,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    keyword,
    siteCode,
    limit,
    page,
    login?.role,
    login?.region,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    pgStatus?.[1],
    searchParams,
    filter?.regionId,
    filter?.areaId,
    filter?.territoryId,
    filter?.supplierId,
  ]);

  useEffect(() => {
    if (pgStatus?.[0] === "pg") {
      getCountTower2();
    } else {
      getCountTower();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCountTower, getCountTower2, pgStatus?.[0], searchParams]);

  useEffect(() => {
    if ((status || keyword || siteCode) && isFirstPage) {
      handlePagination(1, 10);
      handleSetFirstPage(false);
    }
  }, [
    handlePagination,
    handleSetFirstPage,
    isFirstPage,
    keyword,
    siteCode,
    status,
  ]);

  function reRender(val: boolean) {
    if (val) {
      toggle();
      networkData();
      toggleImport();
      confirmToggle();
    }
  }

  const handleRemovePG = (id: string) => {
    dispatch(
      removePGFromTowerTicketAsync({
        data: null,
        others: {
          id,
          reRender,
        },
      })
    );
  };

  const handleTicketCancel = (id: string) => {
    dispatch(
      cancelTowerTicketAsync({
        data: null,
        others: {
          id,
          reRender,
        },
      })
    );
  };

  // PG start manually
  const handlePGStart = (id: string) => {
    dispatch(
      towerTicketStartAsync({
        data: null,
        others: {
          id,
          reRender,
        },
      })
    );
  };

  // PG stop manually
  const handlePGStop = (id: string) => {
    dispatch(
      towerTicketStopAsync({
        data: null,
        others: {
          id,
          reRender,
        },
      })
    );
  };
  interface AlarmLog {
    operator?: string;
    mFStatus?: boolean;
    [key: string]: any; // to support dynamic mFOccuredTime field
  }

  function getDynamicDuration(
    latestAlarmLog?: AlarmLog,
    occurredTimeField: string = "mFOccuredTime",
    op = "gp"
  ): string {
    const operator = latestAlarmLog?.operator?.toLowerCase();
    const isValidOperator = operator === op || operator === "both";
    const hasStatus = !!latestAlarmLog?.mFStatus;
    const occurredRaw = latestAlarmLog?.[occurredTimeField];
    const occurredTime = occurredRaw ? new Date(occurredRaw) : null;

    if (
      !isValidOperator ||
      !hasStatus ||
      !occurredTime ||
      isNaN(occurredTime.getTime())
    ) {
      return "N/A";
    }

    const now = new Date();
    const diff = now.getTime() - occurredTime.getTime();
    if (diff <= 0) return "N/A";

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    const parts: string[] = [];
    if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
    if (hours > 0) parts.push(`${hours} hr${hours > 1 ? "s" : ""}`);
    if (minutes > 0 || parts.length === 0)
      parts.push(`${minutes} min${minutes > 1 ? "s" : ""}`);

    return parts.join(" ");
  }
  const getSdDuration = (val: any, record: any): string => {
    const operator = record?.latestAlarmLog?.operator?.toLowerCase();
    const isTrue =
      (operator === "gp" || operator === "both") &&
      val?.sdStatus &&
      val?.sdOccuredTime;

    if (!isTrue) {
      return "N/A";
    }

    const occurredTime = new Date(val.sdOccuredTime);
    const now = new Date();
    const diff = now.getTime() - occurredTime.getTime();

    if (diff < 0) {
      return "N/A";
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    const parts = [];
    if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
    if (hours > 0) parts.push(`${hours} hr${hours > 1 ? "s" : ""}`);
    if (minutes > 0 || parts.length === 0)
      parts.push(`${minutes} min${minutes > 1 ? "s" : ""}`);

    return parts.join(" ");
  };
  const getSdDurationRobi = (val: any, record: any): string => {
    const operator = record?.latestAlarmLog?.operator?.toLowerCase();
    const isTrue =
      (operator === "robi" || operator === "both") &&
      val?.sdStatus &&
      val?.sdOccuredTime;

    if (!isTrue) {
      return "N/A";
    }

    const occurredTime = new Date(val.sdOccuredTime);
    const now = new Date();
    const diff = now.getTime() - occurredTime.getTime();

    if (diff < 0) {
      return "N/A";
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    const parts = [];
    if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
    if (hours > 0) parts.push(`${hours} hr${hours > 1 ? "s" : ""}`);
    if (minutes > 0 || parts.length === 0)
      parts.push(`${minutes} min${minutes > 1 ? "s" : ""}`);

    return parts.join(" ");
  };
  useEffect(() => {
    if (downloadClicked) {
      const handleDownloadExcel = async () => {
        handleExcelLoading(true);
        let excelData = [] as any[];

        const params: any = { limit: 1000, skip: 0 };

        if (
          login?.role === "region" ||
          login?.role === "finance" ||
          login?.role === "vendor"
        ) {
          params.region = login?.region;
        } else {
          params.region = filter?.regionId;
        }

        params.area = filter?.areaId;
        params.teritory = filter?.territoryId;
        params.supplier = filter?.supplierId;

        if (searchParams.get("today") === "true") {
          params.today = previousDate(formatDate(new Date(), "YYYY-MM-DD"));
        } else {
          params.startDate = filter?.date?.from
            ? previousDate(filter?.date?.from)
            : "";
          params.endDate = filter?.date?.to
            ? previousDate(filter?.date?.to)
            : "";
          params.today = "";
        }

        const filterData =
          status === "unrespond"
            ? { unrespond: true }
            : status === "missCall"
            ? { filter: ["finished"], missCall: true }
            : { filter: status ? [status] : [] };

        await (pgStatus?.[0] === "pg"
          ? AXIOS.get(BASE_API.getAllTower2, {
              params,
            })
          : AXIOS.post(
              BASE_API.getAllTower,
              {
                ...filterData,
                towerName: keyword,
                siteCode,
                // ...filter
              },
              {
                params,
              }
            )
        )
          .then((res) => {
            const responseData = res?.data?.getData as GetAllTowerData[];

            responseData?.forEach((el) => {
              let data = {} as any;
              const startTime =
                el?.towerTicketLogData?.pgAlert?.startTime?.time;
              const stopTime = el?.towerTicketLogData?.pgAlert?.stopTime?.time;
              const pgConnectTime = el?.towerTicketLogData?.pgStart?.time;
              const ttGenerateTime = el?.towerTicketLastData?.createdAt;

              const formattedStartTime = startTime
                ? moment(startTime, "hh:mm A").format("h:mm A")
                : "";
              const formattedStopTime = stopTime
                ? moment(stopTime, "hh:mm A").format("h:mm A")
                : "";
              const formattedPGConnectTime = pgConnectTime
                ? moment(pgConnectTime, "hh:mm A").format("h:mm A")
                : "-";
              const formattedTTGenerateDate =
                formatDateNotNew(ttGenerateTime) ?? "";

              const formattedTTGenerateTime =
                formatTimeNotNew(ttGenerateTime, "h:mm A") ?? "";

              const operator = el?.latestAlarmLog?.operator?.toLowerCase();
              const isTrue =
                (el.latestAlarmLog?.operator?.toLowerCase() === "gp" ||
                  el.latestAlarmLog?.operator?.toLowerCase() === "both") &&
                el.latestAlarmLog?.mFStatus &&
                el.latestAlarmLog?.mFOccuredTime;
              const isTrueR =
                (el.latestAlarmLog?.operator?.toLowerCase() === "robi" ||
                  el.latestAlarmLog?.operator?.toLowerCase() === "both") &&
                el.latestAlarmLog?.mFStatus &&
                el.latestAlarmLog?.mFOccuredTime;

              const mfDuration = getDynamicDuration(
                el.latestAlarmLog,
                "mFOccuredTime"
              );
              const mfDurationr = getDynamicDuration(
                el.latestAlarmLog,
                "mFOccuredTime",
                "robi"
              );
              const dvDuration = getDynamicDuration(
                el.latestAlarmLog,
                "dcOccuredTime"
              );
              const dvDurationr = getDynamicDuration(
                el.latestAlarmLog,
                "dcOccuredTime",
                "robi"
              );
              const isDcTrue =
                (el.latestAlarmLog?.operator?.toLowerCase() === "gp" ||
                  el.latestAlarmLog?.operator?.toLowerCase() === "both") &&
                el.latestAlarmLog?.dcStatus &&
                el.latestAlarmLog?.dcOccuredTime;

              const isSdTru =
                (operator === "gp" || operator === "both") &&
                el.towerTicketLogData?.sdStatus &&
                el.towerTicketLogData?.sdOccuredTime;
              const isSdTrur =
                (operator === "robi" || operator === "both") &&
                el.towerTicketLogData?.sdStatus &&
                el.towerTicketLogData?.sdOccuredTime;

              data["Site Code"] = capitalize(el?.siteCode) ?? "-";
              data["Site Name"] = capitalize(el?.towerName) ?? "-";
              data["Region"] = el?.regionName ?? "-";
              data["Zone"] = el?.areaName ?? "-";
              data["MP"] = el?.teritoryName ?? "-";
              data["EASI ID"] = el?.easiCode ?? "-";
              data["Robi ID"] = el?.robiId ?? "-";
              data["GP ID"] = el?.gpId ?? "-";
              data["PG Runner Name"] =
                el?.PgData?.firstName || el?.PgData?.lastName
                  ? `${el?.PgData?.firstName ?? ""} ${
                      el?.PgData?.lastName ?? ""
                    }`.trim()
                  : "-";
              data[
                "Grid fail time"
              ] = `${formattedStartTime} - ${formattedStopTime}`;
              data["Site Status"] = el?.towerTicketLastData?.status
                ? capitalize(el?.towerTicketLastData?.status)
                : "-";
              data["MF Time (GP)"] = isTrue
                ? `${formatDate(
                    el.latestAlarmLog?.mFOccuredTime
                  )} - ${formatTimeNotNew(
                    el.latestAlarmLog?.mFOccuredTime,
                    "h:mm A"
                  )}`
                : "False";
              data["MF Duration (GP)"] = mfDuration;
              data["DC Low Time (GP)"] = isDcTrue
                ? `${formatDate(
                    el.latestAlarmLog.dcOccuredTime
                  )} - ${formatTimeNotNew(
                    el.latestAlarmLog.dcOccuredTime,
                    "h:mm A"
                  )}`
                : "False";
              data["DC Low Duration (GP)"] = dvDuration;
              data["RMS Voltage (GP)"] =
                operator === "gp" || operator === "both"
                  ? el.latestAlarmLog?.siteDcVoltage ??
                    (el?.latestAlarmLog?.siteDcVoltage
                      ? el?.latestAlarmLog?.siteDcVoltage
                      : " N/A")
                  : "N/A";

              data["SD Time (GP)"] = isSdTru
                ? `${formatDate(
                    el?.towerTicketLogData?.sdOccuredTime
                  )} - ${formatTimeNotNew(
                    el?.towerTicketLogData?.sdOccuredTime,
                    "h:mm A"
                  )}`
                : "False";
              data["SD Duration (GP)"] = getSdDuration(
                el?.towerTicketLogData,
                el
              );
              data["MF Time (Robi)"] = isTrueR
                ? `${formatDate(
                    el?.latestAlarmLog?.mFOccuredTime
                  )} - ${formatTimeNotNew(
                    el?.latestAlarmLog?.mFOccuredTime,
                    "h:mm A"
                  )}`
                : "False";
              data["MF Duration (Robi)"] = mfDurationr;
              data["DC Low Time (Robi)"] = isTrueR
                ? `${formatDate(
                    el?.latestAlarmLog?.dcOccuredTime
                  )} - ${formatTimeNotNew(
                    el?.latestAlarmLog?.dcOccuredTime,
                    "h:mm A"
                  )}`
                : "False";
              data["DC Low Duration (Robi)"] = dvDurationr;
              data["RMS Voltage (Robi)"] =
                operator === "robi" || operator === "both"
                  ? el.latestAlarmLog?.siteDcVoltage ??
                    (el?.latestAlarmLog?.siteDcVoltage
                      ? el?.latestAlarmLog?.siteDcVoltage
                      : " N/A")
                  : "N/A";
              data["SD Time (Robi)"] = isSdTrur
                ? `${formatDate(
                    el?.towerTicketLogData?.sdOccuredTime
                  )} - ${formatTimeNotNew(
                    el?.towerTicketLogData?.sdOccuredTime,
                    "h:mm A"
                  )}`
                : "False";
              data["SD Duration (Robi)"] = getSdDurationRobi(
                el?.towerTicketLogData,
                el
              );

              // data["PG Connect Time"] = formattedPGConnectTime;
              data["Tenant Name"] = el?.contactPersonName ?? "-";
              data["TT No"] = el?.numberOfOperation ?? 0;
              data[
                "TT Generate Time"
              ] = `${formattedTTGenerateDate} - ${formattedTTGenerateTime}`;
              data["TT Status"] = el?.towerTicketLastData?.status
                ? capitalize(el?.towerTicketLastData?.status)
                : "-";
              data["PG Status"] = el?.towerTicketLogData?.missCall
                ? "Missed Call"
                : el?.towerTicketLogData?.status
                ? capitalize(el?.towerTicketLogData?.status)
                : "N/A";

              excelData.push(data);
            });
          })
          .catch(() => {
            handleExcelLoading(false);
            toast("error", "Download fail! Please try again");
          });

        downloadExcel(excelData);
      };

      handleDownloadExcel();
      setDownloadClicked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    downloadClicked,
    filter?.date?.from,
    filter?.date?.to,
    filter?.areaId,
    filter?.regionId,
    filter?.territoryId,
    filter?.supplierId,
    keyword,
    login?.region,
    login?.role,
    pgStatus,
    searchParams,
    siteCode,
    status,
  ]);

  return (
    <>
      {postLoading && uploadCount && totalCount ? (
        <Progress
          className="fixed w-full left-0 right-0 -top-[7px] z-[99999999999999999999999999999999]"
          percent={percentage(uploadCount / 2, totalCount)}
          showInfo={false}
          success={{ strokeColor: "#384DDA" }}
        />
      ) : null}

      {open.type === "Inactive" && (
        <TicketGenerate
          open={open.open}
          data={open?.data}
          handleClose={toggle}
          reRender={reRender}
        />
      )}
      {open.type === "finished" && (
        <Feedback
          open={open.open}
          data={open?.data}
          handleClose={toggle}
          reRender={reRender}
        />
      )}
      {open.type === "Ongoing" && (
        <View open={true} data={open?.data} handleClose={toggle} />
      )}
      {confirmOpen?.type === "REMOVE_PG" && (
        <ConfirmModal
          open={confirmOpen?.open}
          data={confirmOpen?.data}
          handleClose={confirmToggle}
          deleteLoading={updateLoading}
          handleYes={handleRemovePG}
          title={null}
          deleteTitle="Remove Ticket"
          questionText="Are you sure, you want to remove this ticket?"
        />
      )}
      {confirmOpen?.type === "CANCEL_TICKET" && (
        <ConfirmModal
          open={confirmOpen?.open}
          data={confirmOpen?.data}
          handleClose={confirmToggle}
          deleteLoading={updateLoading}
          handleYes={handleTicketCancel}
          title={null}
          deleteTitle="Cancel Ticket"
          questionText="Are you sure to cancel this ticket?"
        />
      )}
      {/* {confirmOpen?.type === "START_TICKET" && (
        <ConfirmModal
          open={confirmOpen?.open}
          data={confirmOpen?.data}
          handleClose={confirmToggle}
          deleteLoading={updateLoading}
          handleYes={handlePGStart}
          title={null}
          deleteTitle="Enable PG"
          questionText="Are sure to enable PG for this ticket?"
        />
      )} */}
      {confirmOpen?.type === "START_TICKET" && (
        <PGEnableModal
          open={confirmOpen?.open}
          data={confirmOpen?.data}
          handleClose={confirmToggle}
        />
      )}
      {confirmOpen?.type === "STOP_TICKET" && (
        <PGDisabledModal
          open={confirmOpen?.open}
          data={confirmOpen?.data}
          handleClose={confirmToggle}
        />
      )}
      {/* {confirmOpen?.type === "STOP_TICKET" && (
        <ConfirmModal
          open={confirmOpen?.open}
          data={confirmOpen?.data}
          handleClose={confirmToggle}
          deleteLoading={updateLoading}
          handleYes={handlePGStop}
          title={null}
          deleteTitle="Disable PG"
          questionText="Are sure to disable PG for this ticket?"
        />
      )} */}
      {open.type === "LOG" && (
        <ViewLog
          title="Site Locator Log"
          locatorTitle="Shilmoon West Para - Mymensingh"
          open={open.open}
          data={open?.data}
          handleClose={toggle}
        />
      )}

      {openImport?.type === "IMPORT" && (
        <ImportExcel
          filter={{ ...filter }}
          open={openImport.open}
          // data={open?.data}
          handleClose={toggleImport}
          reRender={reRender}
          uploadedCount={uploadedCount}
        />
      )}

      <CustomTable<TableData<GetAllTowerData>>
        loading={loading}
        total={towersCount || 0}
        page={page}
        handlePagination={handlePagination}
        columns={isVendor ? columns?.slice(0, columns?.length - 2) : columns}
        dataList={addKeyInArray(allTowers)}
        // isPagination={keyword ? false : true}
        isPagination={true}
      />
    </>
  );
}
